import { Outlet, useSearchParams } from "react-router-dom";
import { RootState } from "../../../store";
import { connect, ConnectedProps } from "react-redux";
import {
  emptyAssetDetails,
  emptyMediaDetails,
} from "@/store/media/media.slice";
import {
  getMedia,
  getAssets,
  deleteMedia,
  getSubtitleList,
} from "@/store/media/media.action";
import { setSoftRefresh, setIsMediaDetailForm } from "@/store/app/app.slice";
import { toggleSideNav, updateModal } from "@/store/app/app.slice";
import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import RenderActionItems from "@/components/MediaList/RenderActionItems";
import { useNavigate } from "react-router-dom";
import {
  MEDIA_DETAILS_MORE_ACTIONS,
  MEDIA_STATUS,
} from "@/utils/constants/uiConstants";
import { Button, CustomSelect, Tooltip } from "../../../components";
import { Alert } from "@/utils/ui/Alert/Alert";
import BackIcon from "../../../assets/icons/svg/arrow_back.svg?react";
import {
  formatTimestampToAgo,
  mediaStatusTooltipText,
  mediaStatusToTextAndColor,
} from "../../../utils";
import { handleCopyId } from "@/utils/utilFunctions/commmonFunctions";
import { EllipsisVertical, TriangleAlert, Copy } from "lucide-react";

import { useLocation } from "react-router-dom";
import Dropdown from "react-bootstrap/Dropdown";
import { useScrollOutside } from "../../../hooks/useScrollOutside";
import MediaDelete, {
  MediaDeleteRef,
} from "@/components/MediaList/MediaDelete";

const Media = ({
  getMedia,
  currentMediaDetailsForEdit,
  softRefresh,
  emptyMediaDetails,
  emptyAssetDetails,
  currentMediaDetailsId,
  toggleSideNav,
  isMandatoryMetaFilled,
  deleteMedia,
  setIsMediaDetailForm,
}: Props) => {
  const { slikeId } = useParams<{ slikeId: string }>();

  const [hasChanges, setHasChanges] = useState(false);

  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [isSaveSecurityDisabled, setIsSaveSecurityDisabled] = useState(false);
  const [isReadOnlyAccess, setIsReadOnlyAccess] = useState(false);
  const [isMediaDeleteModalOpen, setIsMediaDeleteModalOpen] = useState(false);
  const [archiveActionType, setArchiveActionType] = useState<
    "delete" | "archive"
  >("delete");
  const [isDetailsDropdownOpen, setIsDetailsDropdownOpen] = useState(false);
  const [isSecurityDropdownOpen, setIsSecurityDropdownOpen] = useState(false);
  const detailsDropdownRef = useRef<HTMLDivElement>(null);
  const securityDropdownRef = useRef<HTMLDivElement>(null);

  useScrollOutside({
    isOpen: isDetailsDropdownOpen,
    onClose: () => setIsDetailsDropdownOpen(false),
    enabled: true,
    ref: detailsDropdownRef,
  });

  useScrollOutside({
    isOpen: isSecurityDropdownOpen,
    onClose: () => setIsSecurityDropdownOpen(false),
    enabled: true,
    ref: securityDropdownRef,
  });

  const mediaDeleteRef = useRef<MediaDeleteRef>(null);
  const location = useLocation();

  const pathname = location.pathname;

  const sidenavActive = pathname.split("/")[3];

  const handleSaveSecurityDisabled = (disabled: boolean) => {
    setIsSaveSecurityDisabled(disabled);
  };

  const handleConfirmModalOpen = (open: boolean) => {
    setConfirmModalOpen(open);
  };

  const handleSetReadOnlyAccess = (readOnly: boolean) => {
    setIsReadOnlyAccess(readOnly);
  };

  const navigateTo = useNavigate();
  const [searchParams] = useSearchParams();

  const [isWizard, setIsWizard] = useState(false);

  const handleIsWizard = (value: boolean) => {
    setIsWizard(value);
  };

  const handleTempThumbnailImageUrl = (
    url: string | null,
    id: string | null
  ) => {
    console.log("Thumbnail Image change", url, id);
  };

  const handleClose = () => {
    if (hasChanges) {
      handleConfirmModalOpen(true);
    } else {
      const state = location.state as {
        fromChapter?: boolean;
        mediaId?: string;
        chapterId?: string;
        returnPath?: string;
      } | null;

      if (state?.fromChapter) {
        navigateTo(-1);
        return;
      }

      const cleanParams = new URLSearchParams();
      const tabParam = searchParams.get("tab");
      const isDriveTab = tabParam === "drive";
      for (const [key, value] of searchParams.entries()) {
        if (key !== "nextInit" && key !== "navigateOptions" && value) {
          cleanParams.append(key, value);
        }
      }
      emptyAssetDetails();
      if (isDriveTab) {
        // Remove 'tab' param — drive listing is path-based, not tab-param-based
        cleanParams.delete("tab");
        const qs = cleanParams.toString();
        const drivePath =
          currentMediaDetailsForEdit?.asset_type === "shorts"
            ? "/drive/shorts"
            : "/drive/video";
        navigateTo(qs ? `${drivePath}?${qs}` : drivePath);
      } else {
        // Navigate straight to the tab's own route (e.g. /media/video) rather
        // than the bare /media index — landing on the index causes MediaList
        // to redirect itself via navigate(), which remounts it a second time
        // and loses the "returning from detail" cache-reuse flag, causing the
        // page-overflow guard to briefly see a reset count and snap back to
        // page 1 (see the pagination regression this fixes).
        cleanParams.delete("tab");
        const qs = cleanParams.toString();
        const listPath = `/media/${tabParam || "video"}`;
        navigateTo(qs ? `${listPath}?${qs}` : listPath);
      }
    }
  };

  useEffect(() => {
    if (slikeId) {
      emptyMediaDetails();
      emptyAssetDetails();
      getMedia({ id: slikeId }).finally(() => {
        console.log("Media fetched");
      });
    }

    setSoftRefresh(false);

    return () => {
      emptyMediaDetails();
      emptyAssetDetails();
    };
  }, [slikeId, softRefresh]);

  useEffect(() => {
    setIsMediaDetailForm(true);
  }, []);

  useEffect(() => {
    toggleSideNav(true);

    return () => {
      toggleSideNav(false);
    };
  });

  const handleTabChange = (tabName: string, disabled: boolean) => {
    if (!disabled) {
      const currentParams = new URLSearchParams(window.location.search);
      const cleanParams = new URLSearchParams();
      currentParams.forEach((value, key) => {
        if (value && !value.includes("nextInit, navigateOptions")) {
          cleanParams.append(key, value);
        }
      });

      if (tabName === "details" && slikeId) {
        getMedia({ id: slikeId });
      }

      navigateTo(`${tabName}?${cleanParams.toString()}`);
      setHasChanges(false);
      handleTempThumbnailImageUrl(null, null);
    }
  };
  const getCleanParams = () => {
    const cleanParams = new URLSearchParams();
    for (const [key, value] of searchParams.entries()) {
      if (key !== "nextInit" && key !== "navigateOptions" && value) {
        cleanParams.append(key, value);
      }
    }
    return cleanParams;
  };

  const handleSaveClick = () => {
    setStatusValue("0");
    const event = new CustomEvent("nextClick");
    window.dispatchEvent(event);
  };

  const handleSaveAndPublishClick = () => {
    const event = new CustomEvent("next&PublishClick");
    window.dispatchEvent(event);
  };

  const handleUndoClick = () => {
    const event = new CustomEvent("undoClick");
    window.dispatchEvent(event);
  };

  const handleSecurityUndo = () => {
    const event = new CustomEvent("securityUndoClick");
    window.dispatchEvent(event);
  };
  const handleSecuritySave = () => {
    const event = new CustomEvent("securitySaveClick");
    window.dispatchEvent(event);
  };

  function handleDetailsMoreOptions(
    event: React.MouseEvent<HTMLLIElement>
  ): void {
    const value = event.currentTarget.getAttribute("data-value");

    if (value?.toLowerCase() === "delete") {
      setArchiveActionType("delete");
      setIsMediaDeleteModalOpen(true);
    } else if (value?.toLowerCase() === "archive") {
      setArchiveActionType("archive");
      setIsMediaDeleteModalOpen(true);
    }
  }

  const handleFormSubmit = (formData: {
    id: string;
    reason: string;
    notes?: string;
    socials: string[];
  }) => {
    deleteMedia({ ...formData }, archiveActionType);
    setIsMediaDeleteModalOpen(false);
    const deleteState = location.state as { returnPath?: string } | null;
    navigateTo(deleteState?.returnPath ?? `/media`);
  };

  const [statusValue, setStatusValue] = useState(
    currentMediaDetailsForEdit.media_state
      ? String(currentMediaDetailsForEdit.media_state)
      : "0"
  );

  useEffect(() => {
    setStatusValue(
      currentMediaDetailsForEdit.media_state
        ? String(currentMediaDetailsForEdit.media_state)
        : "0"
    );
  }, [currentMediaDetailsForEdit.media_state]);

  const statusOptions = currentMediaDetailsForEdit.media_state
    ? [
        { label: "Active", value: "1" },
        { label: "Inactive", value: "2" },
      ]
    : [
        { label: "Draft", value: "0" },
        { label: "Active", value: "1" },
        { label: "Inactive", value: "2" },
      ];

  return (
    <div className="bg-white z-3 h-100">
      {!isWizard && (
        <div className="mainHeader d-flex gap-2 align-items-center justify-content-between p-2 z-4">
          <div className="d-flex align-items-center">
            <button
              type="button"
              aria-label="Close"
              onClick={handleClose}
              style={{ border: "none", background: "none" }}
            >
              <BackIcon
                className="iconSvg backIcon"
                // height="24px"
              />
            </button>
          </div>
          
          <div
            className="main_form d-flex gap-2 align-items-center me-auto flex-grow-1 overflow-hidden"
            style={{maxWidth: '80%', height: '99%' }}>
            <div className="my-auto" >
              <Tooltip content="Copy ID" placement="bottom">
                <button
                  className="pointer rounded border d-flex align-items-center gap-2 text-capitalize bg-white copy-id-btn"
                  onClick={(e) => handleCopyId(e, currentMediaDetailsId)}
                  style={{
                    padding: "5px 10px",
                    fontWeight: 500,
                  }}
                >
                  {currentMediaDetailsId ? currentMediaDetailsId : "SLIKE ID..."}
                </button>
              </Tooltip>
            </div>
            <div
              className="text-primary fs-5 text-truncate mx-auto"
              style={{ minWidth: 0 }}
            >
              {currentMediaDetailsForEdit.title}
            </div>
          </div>
          {sidenavActive === "details" && (
            <div className="d-flex gap-2 mx-1 align-items-center">
              <Button
                className="btn-sm H"
                type="outline-primary"
                onClick={() => {
                  handleUndoClick();
                }}
                isDisabled={!hasChanges}
                HTMLButtonType="button"
              >
                UNDO CHANGES
              </Button>
              <CustomSelect
                label="Status"
                value={statusValue}
                handleChange={(newValue: any) => {
                  setStatusValue(newValue.value);
                  // TODO: Add logic to persist status change if needed
                }}
                options={statusOptions}
                updatedOptions={statusOptions}
                className="statusField me-2 H"
                maintainSelectHeight="20px"
              />
              {(currentMediaDetailsForEdit?.media_status === 0 ||
                currentMediaDetailsForEdit?.media_status === undefined ||
                currentMediaDetailsForEdit?.content_type === 2) && (
                <Button
                  type="outline-secondary"
                  HTMLButtonType="button"
                  onClick={() => {
                    handleSaveClick();
                  }}
                  isDisabled={!hasChanges || isReadOnlyAccess}
                >
                  SAVE
                </Button>
              )}
              {currentMediaDetailsForEdit?.content_type !== 2 && (
                <Button
                  type="primary"
                  HTMLButtonType="button"
                  className=""
                  onClick={() => {
                    handleSaveAndPublishClick();
                  }}
                  isDisabled={
                    isReadOnlyAccess ||
                    (currentMediaDetailsForEdit?.media_status === undefined ||
                    currentMediaDetailsForEdit?.media_status === 0
                      ? false
                      : !hasChanges)
                  }
                >
                  SAVE &amp;{" "}
                  {currentMediaDetailsForEdit?.media_status === 2 ||
                  currentMediaDetailsForEdit?.media_status === 3 ||
                  currentMediaDetailsForEdit?.media_status === 1
                    ? "Update"
                    : "Publish"}
                </Button>
              )}

              {currentMediaDetailsForEdit?.content_type &&
                currentMediaDetailsForEdit?.content_type !== 2 && (
                  <Dropdown
                    ref={detailsDropdownRef}
                    key={`details-dropdown-${isDetailsDropdownOpen}`}
                    align="end"
                    className="mx-1 px-1"
                    show={isDetailsDropdownOpen && !isReadOnlyAccess}
                    onToggle={(isOpen) =>
                      !isReadOnlyAccess && setIsDetailsDropdownOpen(isOpen)
                    }
                  >
                    <Dropdown.Toggle
                      as="div"
                      bsPrefix="btn p-0 border-0 bg-transparent"
                      style={
                        isReadOnlyAccess
                          ? { opacity: 0.5, pointerEvents: "none" }
                          : undefined
                      }
                    >
                      <EllipsisVertical className="pointer" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ top: "25px", right: "0px" }}>
                      {MEDIA_DETAILS_MORE_ACTIONS.filter(
                        (option: { key: string; icon: string }) => {
                          if (
                            option.key === "Archive" &&
                            currentMediaDetailsForEdit.media_status ===
                              MEDIA_STATUS.MEDIA_STATUS_DRAFT
                          ) {
                            return false;
                          }
                          return true;
                        }
                      ).map((option: { key: string; icon: string }) => (
                        <Dropdown.Item
                          key={option.key}
                          data-value={option.key}
                          onClick={handleDetailsMoreOptions}
                          className="text-capitalize"
                        >
                          {option.key === "Archive" ? "Inactive" : option.key}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                )}
            </div>
          )}
          {sidenavActive === "security" && (
            <>
              <Button
                className="btn-sm H"
                style={{ width: "150px" }}
                type="outline-primary"
                onClick={handleSecurityUndo}
              >
                UNDO CHANGES
              </Button>
              <Button
                type="primary"
                onClick={handleSecuritySave}
                isDisabled={isSaveSecurityDisabled}
                style={{ width: "80px" }}
              >
                SAVE
              </Button>
              <Dropdown
                ref={securityDropdownRef}
                key={`security-dropdown-${isSecurityDropdownOpen}`}
                align="end"
                className="mx-1 px-1 H"
                show={isSecurityDropdownOpen}
                onToggle={(isOpen) => setIsSecurityDropdownOpen(isOpen)}
              >
                <Dropdown.Toggle
                  as="div"
                  bsPrefix="btn p-0 border-0 bg-transparent"
                >
                  <i className="bi bi-three-dots-vertical pointer fs-5" />
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ right: "20px" }}>
                  {MEDIA_DETAILS_MORE_ACTIONS.filter(
                    (option: { key: string; icon: string }) => {
                      if (
                        option.key === "Archive" &&
                        currentMediaDetailsForEdit.media_status ===
                          MEDIA_STATUS.MEDIA_STATUS_DRAFT
                      ) {
                        return false;
                      }
                      return true;
                    }
                  ).map((option: { key: string; icon: string }) => (
                    <Dropdown.Item
                      key={option.key}
                      data-value={option.key}
                      onClick={handleDetailsMoreOptions}
                    >
                      <i className={`${option.icon} me-2 `}></i>
                      {option.key}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </>
          )}
        </div>
      )}
      <div className="mediaedit_content row m-0">
        <div className="mediaedit_content_sidebar col-sm-2 col-md-3 col-lg-2 p-0">
          <div className="border-end col-sm-2 col-md-3 col-lg-2 position-fixed h-100">
            <RenderActionItems
              handleTabChange={handleTabChange}
              hasChanges={hasChanges}
              paramsFilter={getCleanParams()}
              isMandatoryMetaFilled={isMandatoryMetaFilled}
              currentMediaDetailsForEdit={currentMediaDetailsForEdit}
              selectedTab={sidenavActive}
              isReadOnlyAccess={isReadOnlyAccess}
            />
            <div className="mediaInfo bgGrey m-3 p-3">
              <div className="mb-2 fs-6">
                <label className="text-uppercase fw-medium">Modified :</label>
                <div className="d-flex gap-2">
                  <span>
                    {formatTimestampToAgo(
                      currentMediaDetailsForEdit.mtuser || 0
                    )}
                    ,
                  </span>
                  <span>{currentMediaDetailsForEdit?.mtbydata?.name}</span>
                </div>
              </div>

              <div className="mb-2 fs-6">
                <label className="text-uppercase fw-medium">Uploaded :</label>
                <div className="d-flex gap-2">
                  <span>
                    {formatTimestampToAgo(currentMediaDetailsForEdit?.ct || 0)},
                  </span>
                  <span>{currentMediaDetailsForEdit?.ctbydata?.name}</span>
                </div>
              </div>
              {currentMediaDetailsForEdit?.media_ref?.rel && (
                <div className="mb-2 fs-6">
                  <label className="text-uppercase fw-medium">
                    Source Type :
                  </label>
                  <div className="d-flex gap-2">
                    <span>{currentMediaDetailsForEdit?.media_ref?.rel}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className={`mediaedit_content_form col-sm-10 col-md-9 col-lg-10 ${
            isReadOnlyAccess ? "readOnlyMode" : ""
          }`}
        >
          {currentMediaDetailsForEdit?.media_state === 0 && (
            <Alert
              variant="warning"
              className="py-2 d-flex align-items-center position-sticky z-2 H"
              style={{ top: "45px" }}
            >
              <TriangleAlert size={16} className="me-1" />
              Your content is in Draft. Set it to Active to publish.
            </Alert>
          )}
          <Outlet
            context={{
              hasChanges,
              setHasChanges,
              handleTempThumbnailImageUrl,
              handleIsWizard,
              handleSaveSecurityDisabled,
              handleConfirmModalOpen,
              isConfirmModalOpen,
              handleSetReadOnlyAccess,
              goToSocialPublish: () => handleTabChange("social-publish", false),
            }}
          />
          {!isWizard &&
            (sidenavActive === "details" || sidenavActive === "security") && (
              <div
                className="bottomFixedActions d-flex gap-2 align-items-center justify-content-end position-sticky bottom-0 z-4 H"
              >
          {sidenavActive === "details" && (
            <>
              <Button
                className="btn-sm H"
                type="outline-primary"
                onClick={() => {
                  handleUndoClick();
                }}
                isDisabled={!hasChanges}
                HTMLButtonType="button"
              >
                UNDO CHANGES
              </Button>
              {(currentMediaDetailsForEdit?.media_status === 0 ||
                currentMediaDetailsForEdit?.media_status === undefined ||
                currentMediaDetailsForEdit?.content_type === 2) && (
                <Button
                  type="outline-secondary"
                  HTMLButtonType="button"
                  onClick={() => {
                    handleSaveClick();
                  }}
                  isDisabled={!hasChanges || isReadOnlyAccess}
                >
                  SAVE
                </Button>
              )}
              {currentMediaDetailsForEdit?.content_type !== 2 && (
                <Button
                  type="primary"
                  HTMLButtonType="button"
                  onClick={() => {
                    handleSaveAndPublishClick();
                  }}
                  isDisabled={
                    isReadOnlyAccess ||
                    (currentMediaDetailsForEdit?.media_status === undefined ||
                    currentMediaDetailsForEdit?.media_status === 0
                      ? false
                      : !hasChanges)
                  }
                >
                  SAVE &amp;{" "}
                  {currentMediaDetailsForEdit?.media_status === 2 ||
                  currentMediaDetailsForEdit?.media_status === 3 ||
                  currentMediaDetailsForEdit?.media_status === 1
                    ? "Update"
                    : "Publish"}
                </Button>
              )}
              {currentMediaDetailsForEdit?.content_type &&
                currentMediaDetailsForEdit?.content_type !== 2 && (
                  <Dropdown
                    align="end"
                    className="mx-1 px-1"
                    show={isDetailsDropdownOpen && !isReadOnlyAccess}
                    onToggle={(isOpen) =>
                      !isReadOnlyAccess && setIsDetailsDropdownOpen(isOpen)
                    }
                  >
                    <Dropdown.Toggle
                      as="div"
                      bsPrefix="btn p-0 border-0 bg-transparent"
                      style={
                        isReadOnlyAccess
                          ? { opacity: 0.5, pointerEvents: "none" }
                          : undefined
                      }
                    >
                      <EllipsisVertical className="pointer" />
                    </Dropdown.Toggle>
                    <Dropdown.Menu style={{ bottom: "25px", top: "auto", right: "0px" }}>
                      {MEDIA_DETAILS_MORE_ACTIONS.filter(
                        (option: { key: string; icon: string }) => {
                          if (
                            option.key === "Archive" &&
                            currentMediaDetailsForEdit.media_status ===
                              MEDIA_STATUS.MEDIA_STATUS_DRAFT
                          ) {
                            return false;
                          }
                          return true;
                        }
                      ).map((option: { key: string; icon: string }) => (
                        <Dropdown.Item
                          key={option.key}
                          data-value={option.key}
                          onClick={handleDetailsMoreOptions}
                          className="text-capitalize"
                        >
                          {option.key === "Archive" ? "Inactive" : option.key}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                )}
            </>
          )}
          {sidenavActive === "security" && (
            <>
              <Button
                className="btn-sm H"
                style={{ width: "150px" }}
                type="outline-primary"
                onClick={handleSecurityUndo}
              >
                UNDO CHANGES
              </Button>
              <Button
                type="primary"
                onClick={handleSecuritySave}
                isDisabled={isSaveSecurityDisabled}
                style={{ width: "80px" }}
              >
                SAVE
              </Button>
              <Dropdown
                align="end"
                className="mx-1 px-1 H"
                show={isSecurityDropdownOpen}
                onToggle={(isOpen) => setIsSecurityDropdownOpen(isOpen)}
              >
                <Dropdown.Toggle
                  as="div"
                  bsPrefix="btn p-0 border-0 bg-transparent"
                >
                  <i className="bi bi-three-dots-vertical pointer fs-5" />
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ right: "20px", bottom: "20px", top: "auto" }}>
                  {MEDIA_DETAILS_MORE_ACTIONS.filter(
                    (option: { key: string; icon: string }) => {
                      if (
                        option.key === "Archive" &&
                        currentMediaDetailsForEdit.media_status ===
                          MEDIA_STATUS.MEDIA_STATUS_DRAFT
                      ) {
                        return false;
                      }
                      return true;
                    }
                  ).map((option: { key: string; icon: string }) => (
                    <Dropdown.Item
                      key={option.key}
                      data-value={option.key}
                      onClick={handleDetailsMoreOptions}
                    >
                      <i className={`${option.icon} me-2 `}></i>
                      {option.key}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </>
          )}
              </div>
            )}
        </div>
      </div>
      {isMediaDeleteModalOpen && (
        <MediaDelete
          ref={mediaDeleteRef}
          media={currentMediaDetailsForEdit}
          onClose={() => setIsMediaDeleteModalOpen(false)}
          actionType={archiveActionType}
          isOpen={isMediaDeleteModalOpen}
          onFormSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
};

const mapState = (state: RootState) => ({
  currentMediaDetailsForEdit: state.media.mediaDetails.media,
  currentMediaDetailsId: state.media.mediaDetails.media.id,
  softRefresh: state.app.softRefresh,
  isMandatoryMetaFilled: state.media.isMandatoryMetaFilled,
  mediaAssets: state.media.mediaAssets.assets,
});
const mapDispatch = {
  getMedia,
  getAssets,
  emptyAssetDetails,
  emptyMediaDetails,
  toggleSideNav,
  deleteMedia,
  getSubtitleList,
  updateModal,
  setIsMediaDetailForm,
};

const connector = connect(mapState, mapDispatch);
type Props = ConnectedProps<typeof connector>;
export default connector(Media);
